import { Row } from "@tanstack/react-table";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog";

import { Button } from "@/components/ui/button/button";
import { useSalaryModal } from "../salary-modal/use-salary-modal";
import { editAndApproveSalary } from "@/config/mutation/admin";
import { Trash } from "lucide-react";

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const originData = row.original as {
    approved: boolean;
    employee: {
      _id: string;
      name: string;
      email: string;
    };
    createdAt: string | Date;
    netPay: number;
    totalEarnings: number;
    totalDeductions: number;
    notes: string;
    period: string;
    components: {
      type: string;
      amount: number;
      name: string;
      description: string;
    }[];
    id: string;
  };
  const user = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  const { setSalary, onOpenChange } = useSalaryModal();

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: editAndApproveSalary(user),
  });

  const [isOpen, setIsOpen] = useState(false);

  const onDeletion = () => {
    mutate(
      {
        salaryId: originData?.id as string,
        postData: {
          deleted: true,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["employees-salary"],
          });

          toast.success("Salary Deleted!");
        },
        onError: (error) => {
          console.log("ERROR WHILE DELETING SALARY", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <div className="flex items-center gap-4">
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setSalary(originData);
          onOpenChange(true);
        }}
      >
        View
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button size="icon" variant="destructive" className="h-8 w-8">
            <Trash className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete this
              salary.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-end">
            <Button onClick={onDeletion} disabled={isSubmitting}>
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
